import React, { Component } from 'react';
import { number, node, shape, string, bool, object, arrayOf } from 'prop-types';
import compose from 'lodash/flowRight';
import equal from 'fast-deep-equal';
import { Configure } from 'react-instantsearch-core';
import {
    withFeatureFlags,
    withCategory,
    withLandingProduct,
    connectCurrentRefinements,
} from 'connectors';
import { categoryShape, landingProductShape } from 'shapes';
import {
    searchState as searchStateUtils,
    buildPaginationOffsetInfo,
    getCurrentPagination,
} from 'utils';
import { Provider } from './PaginationOffsetContext';

const FACETS = ['has_pro_conditions'];

const DISJUNCTIVE_FACETS = []; // To change into an empty array when we will have full SSR in prod

class PaginationOffset extends Component {
    static propTypes = {
        page: number, // eslint-disable-line react/no-unused-prop-types
        nbResultPerPage: number.isRequired,
        children: node.isRequired,
        // eslint-disable-next-line react/no-unused-prop-types
        searchState: shape({
            query: string,
        }),
        landingProduct: landingProductShape.isRequired,
        items: arrayOf(object),
        category: categoryShape,
        ruleContexts: arrayOf(string),
        hasInCategorySearch: bool.isRequired,
        hasQuickFiltersCardEnabled: bool.isRequired,
        maxPercentageOfInGridElements: number.isRequired,
    };

    static defaultProps = {
        page: 1,
        searchState: {},
        ruleContexts: [],
        items: [],
        category: undefined,
    };

    static getDerivedStateFromProps(props, state) {
        let newState = {};

        if (
            props.page !== state.page ||
            (props.searchState && state.query !== props.searchState.query)
        ) {
            const newCurrentPage =
                props.page !== state.page ? props.page : state.currentPage;

            newState = {
                currentPage: newCurrentPage,
                page: props.page,
            };

            if (props.hasInCategorySearch) {
                newState.query = props.searchState.query || '';
            }
        }

        if (
            searchStateUtils.didUserUpdateSearchState(
                state.searchState,
                props.searchState,
            ) ||
            !equal(state.category, props.category)
        ) {
            newState.paginationOffset = buildPaginationOffsetInfo(
                props.nbResultPerPage,
                props.category,
                props.items,
                props.searchState,
                props.landingProduct.modelId,
                {
                    hasQuickFiltersCardEnabled:
                        props.hasQuickFiltersCardEnabled,
                    maxPercentageOfInGridElements:
                        props.maxPercentageOfInGridElements,
                },
            );
            newState.searchState = props.searchState;
            newState.category = props.category;
        }

        if (Object.keys(newState).length > 0) {
            return newState;
        }
        return null;
    }

    constructor(props) {
        super(props);
        const {
            nbResultPerPage,
            category,
            items,
            searchState,
            landingProduct,
            hasQuickFiltersCardEnabled,
            maxPercentageOfInGridElements,
        } = props;

        this.state = {
            currentPage: 1,
            page: 1,
            paginationOffset: buildPaginationOffsetInfo(
                nbResultPerPage,
                category,
                items,
                searchState,
                landingProduct.modelId,
                {
                    hasQuickFiltersCardEnabled,
                    maxPercentageOfInGridElements,
                },
            ),
            searchState,
            category,
        };
    }

    onPageChange = page => {
        this.setState({
            currentPage: page,
        });
    };

    render() {
        const {
            nbResultPerPage,
            children,
            ruleContexts,
            hasInCategorySearch,
            searchState,
        } = this.props;
        const { currentPage, query, paginationOffset } = this.state;

        const {
            offset: currentPageOffset,
            length: resultsLength,
        } = getCurrentPagination(
            paginationOffset,
            currentPage,
            nbResultPerPage,
        );

        let searchQuery = '';
        if (hasInCategorySearch) searchQuery = query;
        if (searchState.query) searchQuery = searchState.query;

        /**
         * This thing is highly unstable and can generate client side queries if misconfigured.
         * Update it at your own risk :)
         */
        const configProps = searchState.query // We know we are on a search page
            ? {
                  highlightPreTag: '',
                  highlightPostTag: '',
                  hierarchicalFacets: [],
                  facetingAfterDistinct: true,
                  clickAnalytics: true,
                  offset: currentPageOffset,
                  length: resultsLength || 30,
                  page: currentPage,
                  ruleContexts,
                  attributesToHighlight: [],
                  query: searchQuery,
                  maxValuesPerFacet: 50,
                  facets: FACETS,
                  disjunctiveFacets: DISJUNCTIVE_FACETS,
              }
            : {
                  facetingAfterDistinct: true,
                  clickAnalytics: true,
                  disjunctiveFacets: DISJUNCTIVE_FACETS,
                  // optionalFilters /!\ WARNING /!\ There are OptionalFilter module who apply optional filters
                  // if you add optional filters here, you probably override other optional filters
                  facets: FACETS,
                  offset: currentPageOffset,
                  length: resultsLength || 30,
                  page: currentPage,
                  ruleContexts,
                  attributesToHighlight: [],
                  highlightPreTag: '',
                  highlightPostTag: '',
                  query: searchQuery,
              };

        return (
            <Provider
                value={{
                    paginationOffset,
                    onPageChange: this.onPageChange,
                    page: currentPage,
                }}
            >
                <Configure {...configProps} />
                {children}
            </Provider>
        );
    }
}

export default compose(
    withFeatureFlags({
        hasInCategorySearch: 'relevance.in.category.search',
        nbResultPerPage: 'instantSearch.resultsPerPage',
        hasQuickFiltersCardEnabled:
            'browsing.guided.navigation.quickfilters.card',
        maxPercentageOfInGridElements:
            'instantSearch.maxPercentageOfInGridElements',
    }),
    withCategory,
    withLandingProduct,
    connectCurrentRefinements,
)(PaginationOffset);
